import { useEffect, useRef, useState } from 'react';
import { getKBDocumentBlob } from '../api/kb.api';

export function isInternalApiUrl(url: string): boolean {
  return url.startsWith('/api/');
}

export interface UseAuthenticatedBlobResult {
  blob: Blob | null;
  isLoading: boolean;
  error: string | null;
}

/** Fetch the raw `Blob` behind a citation's `url` — an authenticated GET via
 *  `getKBDocumentBlob` for internal `/api/...` URLs (the KB view endpoint,
 *  which 302s to a presigned S3 URL), or a plain `fetch` for anything
 *  external. A sibling of `PDFViewerPanel.tsx`'s own `useAuthenticatedBlobUrl`
 *  — kept as a separate copy, not a shared extraction, so PDF preview
 *  behavior is never touched by this change (see Global Constraints). */
export function useAuthenticatedBlob(
  rawUrl: string,
  isActive: boolean
): UseAuthenticatedBlobResult {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousUrlRef = useRef<string>('');

  useEffect(() => {
    if (!isActive || !rawUrl) {
      setBlob(null);
      setIsLoading(false);
      setError(null);
      previousUrlRef.current = '';
      return;
    }
    if (rawUrl === previousUrlRef.current) return;
    previousUrlRef.current = rawUrl;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setBlob(null);

    const fetchBlob = isInternalApiUrl(rawUrl)
      ? getKBDocumentBlob(rawUrl).then((response) => response.data)
      : fetch(rawUrl).then((response) => {
          if (!response.ok) {
            throw new Error(
              `Fetch failed: ${response.status} ${response.statusText}`
            );
          }
          return response.blob();
        });

    fetchBlob
      .then((result) => {
        if (!cancelled) setBlob(result);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('File preview fetch failed', err);
        setError('Failed to load document.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rawUrl, isActive]);

  return { blob, isLoading, error };
}
